import { useEffect, useRef, useState } from "react";
import Konva from "konva";
import { Group, Circle, Image as KonvaImage, Text, Rect, Line } from "react-konva";
import { useKonvaImage } from "../../hooks/useKonvaImage";
import { useVideoTokenElement } from "../../hooks/useVideoTokenElement";
import { useGameStore } from "../../store/gameStore";

const LIGHT_COLORS = { fire: "#ff9d3d", magic: "#a855f7", electric: "#7dd3fc" };
const TRANSIENT_CLIPS = new Set(["hit", "death", "resurrection", "attack", "attack_melee", "attack_magic", "attack_bow", "attack_throwing"]);

export default function TokenSprite({ token, isSelected, isMoving, onSelect, onDragMove, onDragEnd, canEdit, canManage, onToggleLock, conditionsMap, videoIdentity, isActiveTurn, onContextMenu }) {
  const staticImage = useKonvaImage(token.image_url);
  const imageRef = useRef(null);
  const glowRef = useRef(null);
  const shimmerRef = useRef(null);
  const spriteRef = useRef(null);
  const videoEl = useVideoTokenElement(token.use_video_token ? videoIdentity : null, imageRef);
  const image = videoEl || staticImage;
  const r = (token.size || 50) / 2;
  const isLightSource = token.token_type === "light";
  const lightColor = LIGHT_COLORS[token.light_type] || LIGHT_COLORS.fire;
  const hpPct = token.hp_max ? Math.max(0, Math.min(1, (token.hp_current ?? token.hp_max) / token.hp_max)) : null;
  const isCompanion = token.token_type === "companion";
  const conditions = token.condition_ids || [];
  const isDead = conditions.some((c) => c.slug === "dead");
  const isKnockedOut = !isDead && conditions.some((c) => c.slug === "unconscious");
  const hasLegendaryLoot = token.token_type === "chest" && (token.loot || []).some((i) => i.rarity === "legendary");
  const [shakeX, setShakeX] = useState(0);
  const prevHpRef = useRef(token.hp_current);
  const shoppingGlowRef = useRef(null);
  const torchGlowRef = useRef(null);

  // --- Animated sprite-sheet playback (Token Animation Maker) ------------------------------
  const clips = token.animation_clips || {};
  const isAnimated = !!token.is_animated && Object.keys(clips).length > 0;
  const [activeClip, setActiveClip] = useState("idle");
  const [frameIdx, setFrameIdx] = useState(0);
  const idleImg = useKonvaImage(clips.idle?.sprite_sheet_url);
  const walkImg = useKonvaImage(clips.walk?.sprite_sheet_url);
  const hitImg = useKonvaImage(clips.hit?.sprite_sheet_url);
  const deathImg = useKonvaImage(clips.death?.sprite_sheet_url);
  const resurrectionImg = useKonvaImage(clips.resurrection?.sprite_sheet_url);
  const attackImg = useKonvaImage(clips.attack?.sprite_sheet_url);
  const attackMeleeImg = useKonvaImage(clips.attack_melee?.sprite_sheet_url);
  const attackMagicImg = useKonvaImage(clips.attack_magic?.sprite_sheet_url);
  const attackBowImg = useKonvaImage(clips.attack_bow?.sprite_sheet_url);
  const attackThrowingImg = useKonvaImage(clips.attack_throwing?.sprite_sheet_url);
  const clipImages = {
    idle: idleImg, walk: walkImg, hit: hitImg, death: deathImg, resurrection: resurrectionImg,
    attack: attackImg, attack_melee: attackMeleeImg, attack_magic: attackMagicImg, attack_bow: attackBowImg, attack_throwing: attackThrowingImg,
  };
  const activeMeta = clips[activeClip];
  const activeImg = clipImages[activeClip];
  const isMovingRef = useRef(isMoving);
  useEffect(() => { isMovingRef.current = isMoving; }, [isMoving]);

  const pendingAnimationTrigger = useGameStore((s) => s.pendingAnimationTrigger);
  const lastTriggerIdRef = useRef(null);
  useEffect(() => {
    if (!isAnimated || !pendingAnimationTrigger || pendingAnimationTrigger.token_id !== token.id) return;
    if (lastTriggerIdRef.current === pendingAnimationTrigger.trigger_id) return;
    lastTriggerIdRef.current = pendingAnimationTrigger.trigger_id;
    if (clips[pendingAnimationTrigger.clip]) setActiveClip(pendingAnimationTrigger.clip);
  }, [pendingAnimationTrigger, isAnimated]);

  // Continuous idle/walk state — never interrupts an in-flight transient clip (attack/hit/etc).
  useEffect(() => {
    if (!isAnimated || TRANSIENT_CLIPS.has(activeClip)) return;
    if (isMoving && clips.walk) setActiveClip("walk");
    else if (!isMoving) setActiveClip("idle");
  }, [isMoving, isAnimated]);

  // Frame-cycling loop for whichever clip is currently active.
  useEffect(() => {
    if (!isAnimated || !activeMeta || !activeImg) return;
    let current = 0;
    setFrameIdx(0);
    const frameDurMs = 1000 / (activeMeta.fps || 1);
    let lastTick = 0;
    const layer = spriteRef.current?.getLayer();
    if (!layer) return;
    const anim = new Konva.Animation((frame) => {
      if (!frame || frame.time - lastTick < frameDurMs) return;
      lastTick = frame.time;
      current += 1;
      if (current >= activeMeta.frame_count) {
        if (activeMeta.loop === "loop") {
          current = 0;
        } else if (activeMeta.loop === "hold_last") {
          setFrameIdx(activeMeta.frame_count - 1);
          anim.stop();
          return;
        } else {
          anim.stop();
          setActiveClip(isMovingRef.current && clips.walk ? "walk" : "idle");
          return;
        }
      }
      setFrameIdx(current);
    }, layer);
    anim.start();
    return () => anim.stop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeClip, isAnimated, activeMeta?.frame_count, activeMeta?.fps, activeImg]);
  // --------------------------------------------------------------------------------------------

  // Pulsing glow ring while it's this token's turn in combat.
  useEffect(() => {
    if (!isActiveTurn || !glowRef.current) return;
    const anim = new Konva.Animation((frame) => {
      glowRef.current?.opacity(0.35 + 0.35 * Math.sin((frame?.time || 0) / 400));
    }, glowRef.current.getLayer());
    anim.start();
    return () => anim.stop();
  }, [isActiveTurn]);

  // Shimmering sparkle glow on legendary-rarity treasure chests.
  useEffect(() => {
    if (!hasLegendaryLoot || !shimmerRef.current) return;
    const anim = new Konva.Animation((frame) => {
      const t = frame?.time || 0;
      shimmerRef.current?.opacity(0.4 + 0.4 * Math.sin(t / 220));
      shimmerRef.current?.rotation((t / 30) % 360);
    }, shimmerRef.current.getLayer());
    anim.start();
    return () => anim.stop();
  }, [hasLegendaryLoot]);

  // Warm gold glow while this token is actively browsing a shopkeeper's wares.
  useEffect(() => {
    if (!token.is_shopping || !shoppingGlowRef.current) return;
    const anim = new Konva.Animation((frame) => {
      shoppingGlowRef.current?.opacity(0.3 + 0.35 * Math.sin((frame?.time || 0) / 350));
    }, shoppingGlowRef.current.getLayer());
    anim.start();
    return () => anim.stop();
  }, [token.is_shopping]);

  // Flickering flame halo for any token carrying a light source (torch/candle) — a gentle,
  // non-uniform sine blend so it feels alive rather than a static ring.
  useEffect(() => {
    if (!token.light_radius_ft || !torchGlowRef.current) return;
    const anim = new Konva.Animation((frame) => {
      const t = frame?.time || 0;
      torchGlowRef.current?.opacity(0.35 + 0.25 * Math.sin(t / 140) + 0.1 * Math.sin(t / 55));
    }, torchGlowRef.current.getLayer());
    anim.start();
    return () => anim.stop();
  }, [token.light_radius_ft]);

  // Brief shake feedback whenever this token takes damage (hp_current decreases).
  useEffect(() => {
    const prev = prevHpRef.current;
    prevHpRef.current = token.hp_current;
    if (prev == null || token.hp_current == null || token.hp_current >= prev) return;
    const start = performance.now();
    let raf;
    const step = () => {
      const t = performance.now() - start;
      if (t > 280) return setShakeX(0);
      setShakeX(Math.sin(t / 25) * 6 * (1 - t / 280));
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [token.hp_current]);

  return (
    <Group
      x={token.x + shakeX}
      y={token.y}
      opacity={isDead ? 0.5 : isKnockedOut ? 0.75 : 1}
      draggable={canEdit}
      onClick={onSelect}
      onTap={onSelect}
      onContextMenu={
        onContextMenu
          ? (e) => {
              e.evt.preventDefault();
              onContextMenu();
            }
          : undefined
      }
      onDragMove={(e) => onDragMove?.(token.id, e.target.x(), e.target.y())}
      onDragEnd={(e) => onDragEnd(token.id, e.target.x(), e.target.y(), e.target)}
      data-testid={`token-${token.id}`}
    >
      {token.is_shopping && <Circle ref={shoppingGlowRef} radius={r + 10} stroke="#D4AF37" strokeWidth={4} shadowColor="#D4AF37" shadowBlur={18} opacity={0.4} data-testid={`shopping-glow-${token.id}`} />}
      {!!token.light_radius_ft && <Circle ref={torchGlowRef} radius={r + 6} fill={lightColor} shadowColor={lightColor} shadowBlur={22} opacity={0.4} data-testid={`torch-glow-${token.id}`} />}
      {isActiveTurn && <Circle ref={glowRef} radius={r + 9} stroke="#D4AF37" strokeWidth={3} shadowColor="#D4AF37" shadowBlur={16} opacity={0.5} />}
      {hasLegendaryLoot && (
        <Group ref={shimmerRef} data-testid={`legendary-shimmer-${token.id}`}>
          {[0, 1, 2, 3].map((i) => {
            const angle = (i / 4) * Math.PI * 2;
            return <Circle key={i} x={Math.cos(angle) * (r + 5)} y={Math.sin(angle) * (r + 5)} radius={3} fill="#D4AF37" shadowColor="#D4AF37" shadowBlur={10} />;
          })}
        </Group>
      )}
      <Circle radius={r + 3} stroke={isSelected ? "#D4AF37" : isCompanion ? "#4d7c0f" : "#00000000"} strokeWidth={isSelected ? 3 : isCompanion ? 2 : 0} />
      {isMoving && <Circle radius={r + 7} stroke="#4d7c0f" strokeWidth={2.5} dash={[5, 4]} data-testid={`token-moving-ring-${token.id}`} />}
      {!token.is_approved && <Circle radius={r + 6} stroke="#D90429" strokeWidth={2} dash={[6, 4]} />}
      {token.restrained && <Circle radius={r + 5} stroke="#D4AF37" strokeWidth={2} dash={[3, 3]} data-testid={`restrained-badge-${token.id}`} />}
      {token.locked && <Circle radius={r + 4} stroke="#8a6d1f" strokeWidth={1.5} dash={[2, 5]} opacity={0.7} data-testid={`token-locked-ring-${token.id}`} />}
      <Group
        clipFunc={(ctx) => {
          ctx.arc(0, 0, r, 0, Math.PI * 2, false);
        }}
      >
        {activeMeta && activeImg ? (
          <KonvaImage
            ref={spriteRef}
            image={activeImg}
            x={-r} y={-r} width={r * 2} height={r * 2}
            crop={{ x: frameIdx * activeMeta.frame_size, y: 0, width: activeMeta.frame_size, height: activeMeta.frame_size }}
            data-testid={`token-sprite-anim-${token.id}`}
          />
        ) : image ? (
          <KonvaImage ref={imageRef} image={image} x={-r} y={-r} width={r * 2} height={r * 2} />
        ) : isLightSource ? (
          <>
            <Circle radius={r} fill="#1a1008" />
            <Circle radius={r * 0.55} fill={lightColor} shadowColor={lightColor} shadowBlur={16} opacity={0.9} />
          </>
        ) : token.token_type === "chest" ? (
          <>
            <Rect x={-r} y={-r * 0.3} width={r * 2} height={r * 1.3} fill="#5c3a21" />
            <Rect x={-r} y={-r} width={r * 2} height={r * 0.7} fill="#7a4d2b" />
            <Rect x={-r} y={-r * 0.35} width={r * 2} height={4} fill="#D4AF37" />
          </>
        ) : (
          <>
            <Circle radius={r} fill="#3a3335" />
            <Text text={(token.name || "?").slice(0, 1).toUpperCase()} fontSize={r} fill="#D4AF37" width={r * 2} height={r * 2} x={-r} y={-r * 0.7} align="center" fontFamily="Cormorant Garamond" />
          </>
        )}
      </Group>
      {isDead && (
        <Group data-testid={`token-dead-mark-${token.id}`}>
          <Line points={[-r * 0.6, -r * 0.6, r * 0.6, r * 0.6]} stroke="#D90429" strokeWidth={3} lineCap="round" />
          <Line points={[-r * 0.6, r * 0.6, r * 0.6, -r * 0.6]} stroke="#D90429" strokeWidth={3} lineCap="round" />
        </Group>
      )}
      {isKnockedOut && (
        <Circle data-testid={`token-knocked-out-mark-${token.id}`} radius={r * 0.55} stroke="#D90429" strokeWidth={3.5} shadowColor="#D90429" shadowBlur={6} opacity={0.9} />
      )}
      <Text text={token.name} fontSize={12} fill="#f1e0c5" y={r + 6} x={-r} width={r * 2} align="center" fontFamily="Manrope" />
      {hpPct !== null && (
        <>
          <Rect x={-r} y={-r - 12} width={r * 2} height={4} fill="#3a3335" cornerRadius={2} />
          <Rect x={-r} y={-r - 12} width={r * 2 * hpPct} height={4} fill={hpPct > 0.5 ? "#4d7c0f" : hpPct > 0.25 ? "#D4AF37" : "#D90429"} cornerRadius={2} />
        </>
      )}
      {token.flying && (
        <Group x={r - 4} y={-r - 8} data-testid={`token-flying-badge-${token.id}`}>
          <Line points={[-11, 5, -3, -7, 0, 0]} stroke="#D4AF37" strokeWidth={2} lineCap="round" lineJoin="round" tension={0.4} />
          <Line points={[11, 5, 3, -7, 0, 0]} stroke="#D4AF37" strokeWidth={2} lineCap="round" lineJoin="round" tension={0.4} />
          <Rect x={-15} y={6} width={30} height={13} fill="#1a1008dd" cornerRadius={7} />
          <Text text={`${Math.round(token.altitude_ft || 0)}ft`} x={-15} y={7} width={30} align="center" fontSize={8} fill="#D4AF37" fontFamily="Manrope" />
        </Group>
      )}
      {token.is_shopkeeper && (
        <Group x={-r - 4} y={-r - 8} data-testid={`shopkeeper-badge-${token.id}`}>
          <Circle radius={9} fill="#4d7c0f" stroke="#1a1008" strokeWidth={1} />
          <Text text="$" x={-9} y={-6} width={18} align="center" fontSize={12} fill="#f1e0c5" fontFamily="Manrope" />
        </Group>
      )}
      {canManage && (
        <Group
          x={r - 4}
          y={r - 6}
          data-testid={`token-lock-toggle-${token.id}`}
          onClick={(e) => { e.cancelBubble = true; onToggleLock?.(); }}
          onTap={(e) => { e.cancelBubble = true; onToggleLock?.(); }}
        >
          <Circle radius={9} fill={token.locked ? "#D4AF37" : "#1a1008cc"} stroke="#1a1008" strokeWidth={1} />
          <Line points={[-2.5, -0.5, -2.5, -2.5, 2.5, -2.5, 2.5, -0.5]} stroke={token.locked ? "#1a1008" : "#f1e0c599"} strokeWidth={1.4} lineCap="round" lineJoin="round" tension={0.5} />
          <Rect x={-3.5} y={-1} width={7} height={5} cornerRadius={1} fill={token.locked ? "#1a1008" : "#f1e0c599"} />
        </Group>
      )}
      {conditions.map((c, i) => {
        const color = conditionsMap?.[c.slug] || "#D90429";
        const angle = (i / Math.max(conditions.length, 1)) * Math.PI * 2;
        return <Circle key={c.slug} x={Math.cos(angle) * (r + 3)} y={Math.sin(angle) * (r + 3)} radius={4} fill={color} stroke="#1a1008" strokeWidth={1} />;
      })}
    </Group>
  );
}
